import { Box, Button, Checkbox, FormikField, Input, Text, Tooltip } from '@betterment/samba-react';
import { FormikValidateAfterSubmit } from '@betterment/shared-components';
import { Form, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { FormikFileUpload } from '../../../formik/FormikFileUpload';
import { UploadCsvFormValues } from '../data';

const Schema = Yup.object().shape({
  name: Yup.string().when('useUploadedCsvName', {
    is: false,
    then: schema => schema.required('Name is required'),
    otherwise: schema => schema,
  }),
  csvFile: Yup.mixed().required('CSV file is required'),
  useUploadedCsvName: Yup.boolean(),
});

const DEFAULT_INITIAL_VALUES: UploadCsvFormValues = {
  name: '',
  csvFile: null,
  useUploadedCsvName: false,
};

export type UploadCsvFormProps = {
  initialValues?: Partial<UploadCsvFormValues>;
  onSubmit: (values: UploadCsvFormValues) => Promise<void>;
};

export const getNameWithoutCsvExtension = (fileName: string) => {
  return fileName.replace(/\.csv$/i, '');
};

const FileUploadWithNameSync = () => {
  const { values, setFieldValue } = useFormikContext<UploadCsvFormValues>();

  const handleFileChange = (file: File | null) => {
    if (file && values.useUploadedCsvName) {
      const nameWithoutExtension = getNameWithoutCsvExtension(file.name);
      void setFieldValue('name', nameWithoutExtension);
    }
  };

  return <FormikFileUpload name="csvFile" acceptedFileTypes=".csv" onChange={handleFileChange} />;
};

export const UploadCsvForm = ({ initialValues: initialValuesProp, onSubmit }: UploadCsvFormProps) => {
  return (
    <Box
      css={{
        p: 'space.300',
        borderColor: 'border.weak',
        borderStyle: 'solid',
        borderWidth: 'thin',
        borderRadius: 'medium',
        width: '100%',
        mt: 'space.300',
      }}
    >
      <FormikValidateAfterSubmit<UploadCsvFormValues>
        onSubmit={onSubmit}
        initialValues={{ ...DEFAULT_INITIAL_VALUES, ...initialValuesProp }}
        validationSchema={Schema}
      >
        {({ values, setFieldValue, isSubmitting }) => {
          const handleUseUploadedCsvNameChange = (checked: boolean) => {
            void setFieldValue('useUploadedCsvName', checked);

            if (checked && values.csvFile) {
              const nameWithoutExtension = getNameWithoutCsvExtension(values.csvFile.name);
              void setFieldValue('name', nameWithoutExtension);
            }
          };

          return (
            <Form>
              <FormikField name="csvFile">
                <Box css={{ mb: 'space.300' }}>
                  <FormikField.Label>
                    <Text variant="text100">Upload CSV</Text>
                  </FormikField.Label>
                  <FormikField.Control>
                    <FileUploadWithNameSync />
                  </FormikField.Control>
                  <FormikField.Description>Maximum rows: 500</FormikField.Description>
                  <FormikField.ErrorMessage />
                </Box>
              </FormikField>

              <FormikField name="name">
                <Box>
                  <FormikField.Label>
                    <Box css={{ display: 'inline-flex', alignItems: 'center' }}>
                      <Text variant="text100">Name</Text>
                      <Tooltip>
                        <Tooltip.Trigger asChild={true}>
                          <Tooltip.IconButton name="InfoCircle" css={{ ml: 'space.50' }} />
                        </Tooltip.Trigger>
                        <Tooltip.Content align="center" side="top">
                          <Text>Used to identify this bill in statements and activities</Text>
                        </Tooltip.Content>
                      </Tooltip>
                    </Box>
                  </FormikField.Label>
                  <FormikField.Control>
                    <Input name="name" type="text" disabled={values.useUploadedCsvName || isSubmitting} />
                  </FormikField.Control>
                  <FormikField.ErrorMessage />
                </Box>
              </FormikField>

              <Checkbox
                css={{ mt: 'space.200' }}
                checked={values.useUploadedCsvName}
                onCheckedChange={handleUseUploadedCsvNameChange}
              >
                <Checkbox.Control />
                <Checkbox.Label>
                  <Text variant="text100">Use uploaded CSV name</Text>
                </Checkbox.Label>
              </Checkbox>

              <Button type="submit" variant="solidPrimary" disabled={isSubmitting} css={{ mt: 'space.400' }}>
                Continue
              </Button>
            </Form>
          );
        }}
      </FormikValidateAfterSubmit>
    </Box>
  );
};
